import dayjs from 'dayjs';
import {
  getGridStringOperators,
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridBooleanOperators,
  getGridNumericOperators,
} from '@mui/x-data-grid-premium';
import { PropertyType } from '../config/property';
import { getDynVals } from './DynHelper';
import * as Icons from '@mui/icons-material';
import { JokerFieldTypes } from '../config/joker';
import { filterFieldConfig, KanbanType } from '../config/kanban';

export const objectToArray = (obj) => {
  return Object.entries(obj).map(([key, value]) => ({ field: key, value }));
};

export const generateFilter = (formValues, conditions) => {
  const { like, gte, lte } = conditions;

  const values = objectToArray(formValues);
  const retVal = values.filter((value) => {
    if (value.value !== '' && value.value !== null) {
      if (like?.includes(value.field)) {
        value.like = true;
      } else if (gte?.includes(value.field)) {
        value.gte = true;
      } else if (lte?.includes(value.field)) {
        value.lte = true;
      }
      return value;
    }
    return false;
  });

  return retVal;
};

export const dataGridColumns = (gridCols) => {
  return gridCols.map((col) => {
    return {
      ...col,
      filterable: false,
      headerClassName: 'bg-tableHeaderColor',
    };
  });
};

export const dataGridPremiumColumns = (gridCols) => {
  return gridCols.map((col) => {
    let filterOperators;

    switch (col.type) {
      case 'string':
        filterOperators = getGridStringOperators().filter(
          (op) => !(op?.value === 'isEmpty') && !(op?.value === 'isNotEmpty')
        );
        break;
      case 'number':
        filterOperators = getGridNumericOperators().filter(
          (op) => !(op?.value === 'isEmpty') && !(op?.value === 'isNotEmpty')
        );
        break;
      case 'date':
        filterOperators = getGridDateOperators().filter(
          (op) => !(op?.value === 'isEmpty') && !(op?.value === 'isNotEmpty')
        );
        break;
      case 'dateTime':
        filterOperators = getGridDateOperators().filter(
          (op) => !(op?.value === 'isEmpty') && !(op?.value === 'isNotEmpty')
        );
        break;
      case 'boolean':
        filterOperators = getGridBooleanOperators().filter(
          (op) => !(op?.value === 'isEmpty') && !(op?.value === 'isNotEmpty')
        );
        break;
      case 'singleSelect':
        filterOperators = getGridSingleSelectOperators().filter(
          (op) => !(op?.value === 'isEmpty') && !(op?.value === 'isNotEmpty')
        );
        break;
      default:
        filterOperators = getGridStringOperators().filter(
          (op) => !(op?.value === 'isEmpty') && !(op?.value === 'isNotEmpty')
        );
    }

    return {
      ...col,
      groupable: false,
      aggregable: col.aggregable ?? false,
      headerClassName: 'bg-tableHeaderColor',
      ...(col.isRequired && { filterOperators }),
    };
  });
};

export const generateColumnsFromDynProps = (dynProps, filterable = true) => {
  return dynProps.map((d) => {
    const column = {
      field: `dynamicProperty.dyn_${d.propertyId}`,
      headerName: d.property.name,
      width: 180,
      groupable: false,
      aggregable: false,
      sortable: false,
      filterable,
      headerClassName: 'bg-tableHeaderColor',
    };

    switch (d.property.type) {
      case PropertyType.DATE:
        column.type = 'date';
        column.valueGetter = (_data, row) => {
          const findedDynVal = row.dynamicProperties.find((f) => f.propertyId === d.propertyId);
          const date = findedDynVal?.dateVal ? dayjs(findedDynVal?.dateVal) : null;
          if (date && !date.isValid()) return null;
          return date?.toDate();
        };
        column.renderCell = (data) => {
          const findedDynVal = data.row.dynamicProperties.find((f) => f.propertyId === d.propertyId);
          return findedDynVal?.dateVal ? dayjs(findedDynVal?.dateVal).format('YYYY.MM.DD') : '';
        };

        break;
      case PropertyType.DECIMAL:
        column.type = 'number';
        column.valueGetter = (_data, row) => {
          const findedDynVal = row.dynamicProperties.find((f) => f.propertyId === d.propertyId);
          return findedDynVal?.numberVal ? parseFloat(findedDynVal.numberVal) : '';
        };
        break;
      case PropertyType.NUMBER:
        column.type = 'number';
        column.valueGetter = (_data, row) => {
          const findedDynVal = row.dynamicProperties.find((f) => f.propertyId === d.propertyId);
          return findedDynVal?.numberVal ? parseFloat(findedDynVal.numberVal) : '';
        };
        break;
      case PropertyType.LONGTEXT:
        column.type = 'string';
        column.valueGetter = (_data, row) => {
          const findedDynVal = row.dynamicProperties.find((f) => f.propertyId === d.propertyId);
          return findedDynVal?.longTextVal;
        };
        break;
      case PropertyType.COMBO:
        column.type = 'singleSelect';
        column.valueGetter = (_data, row) => {
          const findedDynVal = row.dynamicProperties.find((f) => f.propertyId === d.propertyId);
          const findedOption = findedDynVal?.property?.propertyOption.find((po) => po.id === findedDynVal.comboBoxVal);
          return findedOption?.id;
        };
        column.valueOptions = generateAutocompleteItems(d.property.propertyOption, 'name', 'id');

        break;
      case PropertyType.TEXT:
        column.type = 'string';
        column.valueGetter = (_data, row) => {
          const findedDynVal = row.dynamicProperties.find((f) => f.propertyId === d.propertyId);
          return findedDynVal?.textVal;
        };
        break;
    }

    return column;
  });
};

export const generateAutocompleteItems = (data = [], labelKey, valueKey) => {
  const labelIsArray = Array.isArray(labelKey);
  return data.map((d) => {
    const label = labelIsArray
      ? labelKey
          .map((key) => d[key])
          .filter(Boolean)
          .join(' - ')
      : `${d[labelKey]}`;
    return {
      label,
      value: d[valueKey],
    };
  });
};

export const cleanValues = (values) => {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value === '' ? null : value]));
};

export const hourToMin = (value) => {
  return Math.floor(value * 60 * 100) / 100;
};

export const minToHour = (value) => {
  return Math.floor((value / 60) * 100) / 100;
};

export const generateSubItems = (items) => {
  const itemMap = {};

  items.forEach((item) => {
    itemMap[item.id] = { ...item, subItems: [] };
  });

  const rootItems = [];

  items.forEach((item) => {
    if (item.parentId) {
      if (itemMap[item.parentId]) {
        itemMap[item.parentId].subItems.push(itemMap[item.id]);
      }
    } else {
      rootItems.push(itemMap[item.id]);
    }
  });

  return rootItems;
};

export const getIcon = (iconName, fontSize = '1.5rem', color = 'grey') => {
  const IconComponent = Icons[iconName];
  return IconComponent ? <IconComponent sx={{ fontSize, color }} /> : null;
};

export const clearEditMode = (apiRef) => {
  const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

  for (const rowId of rowsInEditMode) {
    apiRef.current.stopRowEditMode({ id: rowId });
  }
};

export const getJokerFieldType = (f) => {
  let fieldType = JokerFieldTypes.TEXT;
  if (f.valuesToForm && f.valuesToForm.length > 0) {
    fieldType = JokerFieldTypes.SELECT;
  } else if (f.field.startsWith('joker_num') || f.field.startsWith('joker_int')) {
    fieldType = JokerFieldTypes.NUMBER;
  } else if (f.field.startsWith('joker_date')) {
    fieldType = JokerFieldTypes.DATE;
  }
  return fieldType;
};

export const avg = (nums) => {
  const s = nums.reduce((a, b) => a + b, 0);
  return Math.round(s / nums.length);
};

export const getContrastColor = (hex) => {
  if (!hex) return '#000';
  const r = parseInt(hex.substr(1, 2), 16);
  const g = parseInt(hex.substr(3, 2), 16);
  const b = parseInt(hex.substr(5, 2), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 128 ? '#000' : '#fff';
};

export const getKanbanFooterTitle = (type) => {
  switch (type) {
    case KanbanType.TASK:
      return 'Feladatok száma';
    case KanbanType.PROJECT:
      return 'Projektek száma';
    case KanbanType.RESOURCE:
      return 'Dolgozók száma';
    case KanbanType.COMPANY:
      return 'Cégek száma';
    case KanbanType.FEEDBACK:
      return 'Feedbackek száma';
    case KanbanType.TASKLIST:
      return 'Feladatok száma';
    default:
      return 'Összesen';
  }
};

export const getKanbanFieldsByControl = (type, control) =>
  Object.entries(filterFieldConfig.fields).filter(([, cfg]) => cfg.types.includes(type) && cfg.control === control);

export const generateDefaultTime = (date, timeConfig) => {
  let dt = dayjs(date);

  if (!timeConfig) {
    return dt.second(0).millisecond(0);
  }

  const defTime = dayjs(timeConfig);

  const offsetMinutes = dt.utcOffset();
  const offsetHours = Math.abs(offsetMinutes / 60);

  const adjustedTime = defTime.subtract(offsetHours, 'hour');

  return dt.hour(adjustedTime.hour()).minute(adjustedTime.minute()).second(0).millisecond(0);
};

export const parseNumberFromText = (val) => {
  if (val == null || val == '') return NaN;
  if (typeof val === 'number') return val;

  let s = String(val).trim();

  if (s.includes(',') && !s.includes('.')) {
    s = s.replace(',', '.');
  }

  s = s.replace(/[^\d.-]/g, '');

  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};
